/**
 * NewsPreviewPage — Renderiza una noticia accedida vía preview_token.
 *
 * Funciona para borradores y noticias en estado "link privado". El token actúa
 * como llave de acceso, así que no requiere sesión iniciada.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, Loader2, AlertCircle, Calendar } from 'lucide-react';
import { api, absoluteUrl } from '../lib/api';
import { normalizeHtml } from '../lib/htmlText';
import RichContentBlocks from '../components/RichContentBlocks';

const STATUS_LABEL = {
  draft:        { label: 'Borrador',     classes: 'bg-gray-100 text-gray-800 border-gray-300' },
  private_link: { label: 'Link privado', classes: 'bg-amber-50 text-amber-800 border-amber-200' },
  public:       { label: 'Público',      classes: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

export default function NewsPreviewPage() {
  const { token } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/news/preview/${token}`)
      .then(({ data }) => { if (alive) setItem(data); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || 'No se pudo cargar la vista previa'); });
    return () => { alive = false; };
  }, [token]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="news-preview-error">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="text-rose-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-charcoal-800 mb-2">No se encontró la vista previa</h1>
          <p className="text-sm text-charcoal-500">{error}</p>
        </div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="news-preview-loading">
        <Loader2 size={28} className="animate-spin text-cmic-500" />
      </div>
    );
  }

  const status = item.status || 'public';
  const meta = STATUS_LABEL[status] || STATUS_LABEL.public;

  return (
    <div data-testid="news-preview-page">
      {/* Banner contextual cuando NO es público */}
      {status !== 'public' && (
        <div className={`border-y px-5 py-2.5 text-center text-xs font-medium ${meta.classes}`} data-testid="news-preview-status-banner">
          <Eye size={12} className="inline-block mr-1" />
          Estás viendo este contenido como <strong>{meta.label.toLowerCase()}</strong>.
          Cualquier persona con este enlace puede verlo, pero no aparece en el sitio público.
        </div>
      )}

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-10 break-words">
        {item.image_url && (
          <div className="rounded-2xl overflow-hidden mb-8 bg-gray-100">
            <img src={absoluteUrl(item.image_url)} alt={item.title} className="w-full h-auto max-h-[480px] object-cover" />
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl font-bold text-charcoal-900 leading-tight break-words">{item.title}</h1>
        {item.published_at && (
          <div className="mt-3 text-sm text-charcoal-500 inline-flex items-center gap-1.5">
            <Calendar size={14} />
            {new Date(item.published_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        )}
        {item.summary && (
          <p className="mt-5 text-base text-charcoal-600 leading-relaxed italic border-l-4 border-cmic-300 pl-4 break-words">
            {item.summary}
          </p>
        )}
        {item.content && (
          <div
            className="prose prose-charcoal max-w-none prose-headings:text-charcoal-800 prose-a:text-cmic-600 prose-strong:text-charcoal-800 prose-img:rounded-xl prose-img:max-w-full mt-6 break-words"
            dangerouslySetInnerHTML={{ __html: normalizeHtml(item.content) }}
          />
        )}
        <RichContentBlocks blocks={item.content_blocks} />
      </article>
    </div>
  );
}
